import { useState } from 'react';
import type { RosterSelection } from './useRosterSelection';
import { formatNamesForTruePeopleSearch } from './useRosterSelection';
import { leadListApi, type LeadListSourceKind } from '../services/leadListApi';

interface SelectionBarProps {
  selection: RosterSelection;
  sourceLabel: string;       // "AOI: T4N R62W Sec 20" or "Docket 260400104"
  // Widened to accept the two Wyoming source kinds. The
  // leadListApi.addItems contract handles all four uniformly; this
  // bar is a thin pass-through.
  source_kind: LeadListSourceKind;
  source_id: string;
  onPush: () => void;
  /** When provided, renders a "→ Skip trace (N)" button next to Push that
   *  hands off the selection to the external recorder skip-trace agent.
   *  Only the AOI detail page passes this (the payload needs per-owner
   *  doc_numbers / TRS + the AOI county). */
  onSkipTrace?: () => void;
  /** Disables + relabels the skip-trace button while a submit is in flight. */
  skipTraceBusy?: boolean;
  /** "Load results to Close" toggle, shown next to the skip-trace button.
   *  Controlled by the AOI page; defaults CHECKED there. */
  loadToClose?: boolean;
  onLoadToCloseChange?: (v: boolean) => void;
}

// Sticky bottom bar visible when ≥1 row is selected. Shows counts,
// the Copy-names button, and the Push to Close trigger.
export function SelectionBar({ selection, sourceLabel, source_kind, source_id, onPush, onSkipTrace, skipTraceBusy, loadToClose, onLoadToCloseChange }: SelectionBarProps) {
  const [copied, setCopied] = useState(false);
  const [addingToLeadList, setAddingToLeadList] = useState(false);
  const [leadListToast, setLeadListToast] = useState<string | null>(null);

  async function addToLeadList() {
    setAddingToLeadList(true);
    setLeadListToast(null);
    try {
      const r = await leadListApi.addItems({
        party_ids: selection.selectedRows.map(s => s.party_id),
        source_kind,
        source_id,
      });
      const parts: string[] = [];
      if (r.added > 0) parts.push(`Added ${r.added}`);
      if (r.already_in_list > 0) parts.push(`${r.already_in_list} already on list`);
      setLeadListToast(parts.join(' · ') || 'No changes');
      setTimeout(() => setLeadListToast(null), 2500);
    } catch (e: any) {
      setLeadListToast(`Failed: ${e?.message ?? 'unknown'}`);
      setTimeout(() => setLeadListToast(null), 4000);
    } finally {
      setAddingToLeadList(false);
    }
  }

  async function copyNames() {
    const text = formatNamesForTruePeopleSearch(selection.selectedRows);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API can fail in some contexts; fall back to a textarea-select.
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  }

  if (selection.count === 0) return null;

  return (
    <div className="fixed bottom-0 left-[220px] right-0 z-30 border-t border-accent/40 bg-bg-card-soft/95 backdrop-blur px-8 py-3 flex items-center gap-6">
      <div className="text-[12px] text-text-dim font-mono">
        <span className="text-text">{selection.count}</span> selected on <span className="text-text">{sourceLabel}</span>
        <span className="text-text-faint mx-2">·</span>
        <span className="text-text">{selection.withPhone}</span> with phone
        <span className="text-text-faint mx-2">·</span>
        <span className="text-text">{selection.withAddress}</span> with current address
        {selection.suggested > 0 && (
          <>
            <span className="text-text-faint mx-2">·</span>
            <span className="text-red">{selection.suggested} suggested (must Confirm/Reject before push)</span>
          </>
        )}
      </div>
      <div className="ml-auto flex items-center gap-2">
        {leadListToast && (
          <span className="text-[11px] text-text-dim font-mono mr-1">{leadListToast}</span>
        )}
        <button onClick={selection.clear}
          className="px-3 py-1.5 text-[12px] text-text-faint border border-border rounded hover:text-text">
          Clear
        </button>
        <button onClick={copyNames}
          className="px-3 py-1.5 text-[12px] text-text border border-border rounded hover:bg-bg-hover">
          {copied ? `Copied ${selection.count} ✓` : `Copy ${selection.count} names ↗`}
        </button>
        <button onClick={addToLeadList}
          disabled={addingToLeadList || selection.count === 0}
          title="Add to lead list for batched push later"
          className="px-3 py-1.5 text-[12px] text-text border border-border rounded hover:bg-bg-hover disabled:opacity-50">
          {addingToLeadList ? 'Adding…' : `+ Lead list`}
        </button>
        {onSkipTrace && (
          <label className="flex items-center gap-1.5 text-[11px] text-text-dim cursor-pointer select-none"
            title="Load the skip-trace results into Close when the run finishes">
            <input type="checkbox" checked={loadToClose ?? true}
              onChange={e => onLoadToCloseChange?.(e.target.checked)}
              className="accent-accent" />
            Load to Close
          </label>
        )}
        {onSkipTrace && (
          <button onClick={onSkipTrace}
            disabled={skipTraceBusy}
            title="Hand the selected owners to the recorder skip-trace agent"
            className="px-3 py-1.5 text-[12px] text-blue border border-blue/40 rounded hover:bg-blue/10 disabled:opacity-50 disabled:cursor-not-allowed">
            {skipTraceBusy ? 'Submitting…' : `→ Skip trace (${selection.count})`}
          </button>
        )}
        <button onClick={onPush}
          disabled={selection.suggested > 0}
          title={selection.suggested > 0 ? 'Resolve suggested rows first' : 'Push selection to Close as Leads + Opportunities'}
          className="px-3 py-1.5 text-[12px] bg-accent text-bg font-medium rounded hover:bg-accent-bright disabled:opacity-50 disabled:cursor-not-allowed">
          Push {selection.count} to Close →
        </button>
      </div>
    </div>
  );
}
